/**
 * EXPERT RULES (EXPERT-NETWORKS.md §2, E2) — a rule leaf's severity, the request kinds it governs, and HOW it is
 * checked (on the plan, after the run). Domain-agnostic: whatever the Expert Network authored is shown as-is.
 */
import React from 'react';

export const SEVERITY = {
  block: ['Blocks', 'bg-rose-50 text-rose-700 ring-rose-200', 'Must hold before any change lands'],
  warn: ['Warns', 'bg-amber-50 text-amber-800 ring-amber-200', 'Flagged when broken; the change can still proceed'],
  advise: ['Advises', 'bg-gray-50 text-gray-600 ring-gray-200', 'Guidance — not enforced'],
};

export function SeverityBadge({ rule }) {
  if (!rule || !SEVERITY[rule.severity]) return null;
  const [label, cls, hint] = SEVERITY[rule.severity];
  return <span data-testid="rule-severity" title={hint}
    className={`rounded px-1.5 py-px text-[10px] font-medium ring-1 ${cls}`}>{label}</span>;
}

/** BASE / OVERLAY (EXPERT-NETWORKS.md §4, E3): where a deployed rule came from. */
export function RuleSourceChip({ rule }) {
  const src = String(rule?.source || '');
  const m = /^base@(\d+)$/.exec(src);
  // A ROOM'S CHANGE (EXPERT-NETWORKS.md §12): this room adjusted the rule — say why (and whether it went to the network)
  const ch = rule?.change;
  const changed = ch ? (
    <span data-testid="rule-change-chip" title={[ch.reason, ch.by, ch.proposalId ? 'suggested to the network' : '']
      .filter(Boolean).join(' · ')}
      className="rounded bg-sky-50 px-1.5 py-px text-[10px] font-medium text-sky-700">
      {ch.kind === 'add' ? 'Added here' : 'Changed here'}</span>) : null;
  if (!m && src !== 'overlay') return changed;
  return <>
    <span data-testid="rule-source" title={m ? 'From the Expert Network this was deployed from' : 'Your own version'}
      className="rounded bg-indigo-50 px-1.5 py-px text-[10px] font-medium text-indigo-700">{m ? `Base v${m[1]}` : 'Yours'}</span>
    {changed}
  </>;
}

/** An upgrade changed (or retired) a rule this room had edited — theirs is kept until they answer. */
export function BaseUpdateReview({ rule, onDecide, busy = false }) {
  const u = rule?.baseUpdate;
  if (!u) return null;
  const b = u.rule || {};
  return (
    <div className="mt-1.5 max-w-2xl rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-1.5 text-[11px] text-amber-900"
      data-testid="rule-base-review">
      <p className="font-medium">
        {u.retired ? `Version ${u.version} of the base no longer has this rule.`
          : `Version ${u.version} of the base changed this rule — you edited it, so yours is kept.`}
      </p>
      {!u.retired && b.statement ? <p className="mt-0.5 text-amber-800">Base now says: {b.statement}</p> : null}
      {onDecide ? (
        <div className="mt-1 flex gap-2">
          <button type="button" disabled={busy} onClick={() => onDecide('take')} data-testid="rule-base-take"
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
            {u.retired ? 'Switch it off' : "Use the base's version"}
          </button>
          <button type="button" disabled={busy} onClick={() => onDecide('keep')} data-testid="rule-base-keep"
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-amber-800 hover:bg-amber-100 disabled:opacity-50">
            Keep mine
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function RuleChecks({ rule }) {
  if (!rule) return null;
  const c = rule.check || {};
  return (
    <div className="mt-1 max-w-2xl space-y-0.5 text-[11px] text-gray-600" data-testid="rule-checks">
      {c.plan ? <p><span className="font-medium text-gray-700">Checked before:</span> {c.plan}</p> : null}
      {c.run ? <p><span className="font-medium text-gray-700">Checked after:</span> {c.run}</p> : null}
      {!c.plan && !c.run ? <p className="text-gray-400">No check yet — this rule can only advise.</p> : null}
      {(rule.appliesTo || []).length ? (
        <p><span className="font-medium text-gray-700">Applies to:</span> {rule.appliesTo.join(', ')}</p>
      ) : null}
    </div>
  );
}

export function RuleEditor({ value, onChange }) {
  const v = value || {};
  const c = v.check || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const noCheck = !(c.plan || '').trim() && !(c.run || '').trim();
  return (
    <div className="mt-2 grid max-w-2xl gap-1.5 text-[11px]" data-testid="rule-editor">
      <label className="flex items-center gap-2 text-gray-600">Severity
        <select value={v.severity || 'warn'} onChange={(e) => set({ severity: e.target.value })} data-testid="rule-edit-severity"
          className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px]">
          {Object.entries(SEVERITY).map(([k, [label]]) => (
            <option key={k} value={k} disabled={k === 'block' && noCheck}>{label}{k === 'block' && noCheck ? ' (needs a check)' : ''}</option>
          ))}
        </select>
      </label>
      <input value={c.plan || ''} placeholder="How to check it before a change" data-testid="rule-edit-plan"
        onChange={(e) => set({ check: { ...c, plan: e.target.value } })}
        className="rounded border border-gray-300 px-2 py-1" />
      <input value={c.run || ''} placeholder="How to check it after the change" data-testid="rule-edit-run"
        onChange={(e) => set({ check: { ...c, run: e.target.value } })}
        className="rounded border border-gray-300 px-2 py-1" />
      <input value={(v.appliesTo || []).join(', ')} placeholder="Applies to (comma-separated request kinds)"
        onChange={(e) => set({ appliesTo: e.target.value.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean) })}
        className="rounded border border-gray-300 px-2 py-1" />
    </div>
  );
}
